// Spoken answers are compared by how they sound, not how they are spelled:
// speech cannot tell ι/η/υ/ει/οι apart, nor ο/ω or αι/ε, and the recognizer
// sometimes writes a short Greek word in Cyrillic or Latin letters ("ηρεμώ"
// came back as "Иремо" and "Iremo"). Every script is reduced to one rough
// Latin sound key, so all three spellings meet.

const GREEK_DIGRAPHS: [string, string][] = [
    ['ου', 'u'], ['αι', 'e'], ['ει', 'i'], ['οι', 'i'], ['υι', 'i'],
    ['αυ', 'av'], ['ευ', 'ev'], ['μπ', 'b'], ['ντ', 'd'], ['γκ', 'g'], ['γγ', 'g'],
    ['τσ', 'ts'], ['τζ', 'dz'],
];

const GREEK_LETTERS: Record<string, string> = {
    α: 'a', β: 'v', γ: 'g', δ: 'd', ε: 'e', ζ: 'z', η: 'i', θ: 'th', ι: 'i', κ: 'k', λ: 'l', μ: 'm',
    ν: 'n', ξ: 'ks', ο: 'o', π: 'p', ρ: 'r', σ: 's', ς: 's', τ: 't', υ: 'i', φ: 'f', χ: 'h', ψ: 'ps', ω: 'o',
};

const CYRILLIC_LETTERS: Record<string, string> = {
    а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'o', ж: 'zh', з: 'z', и: 'i', й: 'i', к: 'k',
    л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'ts',
    ч: 'ch', ш: 'sh', щ: 'sh', ъ: '', ы: 'i', ь: '', э: 'e', ю: 'u', я: 'a', і: 'i', ї: 'i', є: 'e',
};

/** Lower case, no accents or punctuation, single spaces. */
export function plainText(text: string): string {
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9Ͱ-ϿЀ-ӿ\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function wordKey(word: string): string {
    let key = '';
    for (let i = 0; i < word.length; i++) {
        const pair = word.slice(i, i + 2);
        const digraph = GREEK_DIGRAPHS.find(([greek]) => greek === pair);
        if (digraph !== undefined) {
            key += digraph[1];
            i++;
            continue;
        }
        const char = word[i];
        key += GREEK_LETTERS[char] ?? CYRILLIC_LETTERS[char] ?? (/[a-z0-9]/.test(char) ? char : '');
    }
    // Doubled letters sound single (άλλος, суббота, coffee).
    return key.replace(/(.)\1+/g, '$1');
}

/** The sound key of every word: "Θα γράψω!" → ["tha", "grapso"]. */
export function soundWords(text: string): string[] {
    return plainText(text)
        .split(' ')
        .map(wordKey)
        .filter(word => word.length > 0);
}

export function soundKey(text: string): string {
    return soundWords(text).join(' ');
}

export function editDistance(a: string, b: string): number {
    const previous = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i++) {
        let diagonal = previous[0];
        previous[0] = i;
        for (let j = 1; j <= b.length; j++) {
            const above = previous[j];
            previous[j] = Math.min(
                previous[j] + 1,
                previous[j - 1] + 1,
                diagonal + (a[i - 1] === b[j - 1] ? 0 : 1),
            );
            diagonal = above;
        }
    }
    return previous[b.length];
}

/** How many sound edits a phrase of this length may be off by and still count. */
export function tolerance(key: string): number {
    const length = key.replace(/ /g, '').length;
    if (length < 5) return 0;
    if (length < 10) return 1;
    return 2;
}
